import { Api } from '../../../../services/interceptor';
import { QUERY_KEY } from '../../../../services/queryEndPoint';
import type {
  IJoinLeagueRequest,
  IUmpireRequest,
  IScorerRequest,
  IPlayerByLeagueForRequest,
  IProfileClaim,
} from '../types/request';

function unwrapArray<T>(raw: unknown): T[] {
  const arr = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && 'data' in raw
      ? (raw as { data: unknown }).data
      : [];
  return Array.isArray(arr) ? arr.filter((x) => x != null && typeof x === 'object') : [];
}

/** GET /searchPlayersByLeague?leagueId= */
export async function fetchSearchPlayersByLeague(
  leagueId: string
): Promise<IPlayerByLeagueForRequest[]> {
  const res = await Api.get(QUERY_KEY.getAllPlayersByLeague, { params: { leagueId } });
  return unwrapArray<IPlayerByLeagueForRequest>(res?.data);
}

/**
 * Fetch all request-related data for a league.
 * - Player: GET /getJoinLeagueRequests/{leagueId}
 * - Umpire: GET /league/umpire/{leagueId}?status=REQUEST
 * - Scorer: GET /league/scorer/{leagueId}?status=REQUEST
 * - All Players: GET /searchPlayersByLeague?leagueId={leagueId}
 * - Claims: GET /getProfileClaimsByLeagueId/{leagueId}
 */
export async function fetchJoinLeagueRequests(
  leagueId: string
): Promise<{
  playerRequests: IJoinLeagueRequest[];
  umpireRequests: IUmpireRequest[];
  scorerRequests: IScorerRequest[];
  allPlayers: IPlayerByLeagueForRequest[];
  profileClaims: IProfileClaim[];
}> {
  const [playerSettled, umpireSettled, scorerSettled, allPlayersSettled, claimsSettled] =
    await Promise.allSettled([
      Api.get(`${QUERY_KEY.getJoinLeagueRequests}/${leagueId}`),
      Api.get(`${QUERY_KEY.getUmpireRequestsForLeague}/${leagueId}`, {
        params: { status: 'REQUEST' },
      }),
      Api.get(`${QUERY_KEY.getScorerRequestsForLeague}/${leagueId}`, {
        params: { status: 'REQUEST' },
      }),
      Api.get(`${QUERY_KEY.getAllPlayersByLeague}`, { params: { leagueId } }),
      Api.get(`${QUERY_KEY.getProfileClaimsByLeagueId}/${leagueId}`),
    ]);

  const playerRequests =
    playerSettled.status === 'fulfilled'
      ? unwrapArray<IJoinLeagueRequest>(playerSettled.value?.data)
      : [];
  const umpireRequests =
    umpireSettled.status === 'fulfilled'
      ? unwrapArray<IUmpireRequest>(umpireSettled.value?.data)
      : [];
  const scorerRequests =
    scorerSettled.status === 'fulfilled'
      ? unwrapArray<IScorerRequest>(scorerSettled.value?.data)
      : [];
  const allPlayers =
    allPlayersSettled.status === 'fulfilled'
      ? unwrapArray<IPlayerByLeagueForRequest>(allPlayersSettled.value?.data)
      : [];
  const profileClaims =
    claimsSettled.status === 'fulfilled'
      ? unwrapArray<IProfileClaim>(claimsSettled.value?.data)
      : [];

  if (playerSettled.status === 'rejected') {
    console.error('[fetchJoinLeagueRequests] Player requests failed:', playerSettled.reason);
  }
  if (umpireSettled.status === 'rejected') {
    console.error('[fetchJoinLeagueRequests] Umpire requests failed:', umpireSettled.reason);
  }
  if (scorerSettled.status === 'rejected') {
    console.error('[fetchJoinLeagueRequests] Scorer requests failed:', scorerSettled.reason);
  }
  if (allPlayersSettled.status === 'rejected') {
    console.error('[fetchJoinLeagueRequests] All players failed:', allPlayersSettled.reason);
  }
  if (claimsSettled.status === 'rejected') {
    console.error('[fetchJoinLeagueRequests] Profile claims failed:', claimsSettled.reason);
  }

  const allRejected =
    playerSettled.status === 'rejected' &&
    umpireSettled.status === 'rejected' &&
    scorerSettled.status === 'rejected' &&
    allPlayersSettled.status === 'rejected' &&
    claimsSettled.status === 'rejected';
  if (allRejected) {
    throw playerSettled.reason ?? umpireSettled.reason ?? scorerSettled.reason;
  }

  return {
    playerRequests,
    umpireRequests,
    scorerRequests,
    allPlayers,
    profileClaims,
  };
}

export type UmpireScorerBulkRequestStatus = 'REQUEST' | 'ACCEPT' | 'REJECT' | 'CANCEL';

export type PlayerLeagueBulkRequestStatus = 'APPROVE' | 'REJECT';

function normalizeBulkResponse(data: unknown): string {
  if (typeof data === 'string') return data;
  if (data != null && typeof data === 'object' && 'message' in data) {
    return String((data as { message: unknown }).message);
  }
  return data != null ? String(data) : '';
}

/** POST /league/bulkChangeStatusOfUmpire — body is an array of { playerId, leagueId, requestStatus } */
export async function bulkChangeUmpireStatus(
  leagueId: string,
  entries: { playerId: string; requestStatus: UmpireScorerBulkRequestStatus }[]
): Promise<string> {
  const body = entries.map((e) => ({
    playerId: e.playerId,
    leagueId,
    requestStatus: e.requestStatus,
  }));
  const res = await Api.post(QUERY_KEY.bulkChangeStatusOfUmpire, body);
  return normalizeBulkResponse(res.data);
}

/** POST /league/bulkChangeStatusOfScorer */
export async function bulkChangeScorerStatus(
  leagueId: string,
  entries: { playerId: string; requestStatus: UmpireScorerBulkRequestStatus }[]
): Promise<string> {
  const body = entries.map((e) => ({
    playerId: e.playerId,
    leagueId,
    requestStatus: e.requestStatus,
  }));
  const res = await Api.post(QUERY_KEY.bulkChangeStatusOfScorer, body);
  return normalizeBulkResponse(res.data);
}

/** POST /league/bulkChangeStatusOfPlayer */
export async function bulkChangePlayerStatus(
  leagueId: string,
  playerIds: string[],
  playerLeagueRequestStatus: PlayerLeagueBulkRequestStatus
): Promise<string> {
  const res = await Api.post(QUERY_KEY.bulkChangeStatusOfPlayer, {
    playerIds,
    leagueId,
    playerLeagueRequestStatus,
  });
  return normalizeBulkResponse(res.data);
}
